import { SceneId, ServerMessagePayload } from '@common/api';
import { LineOfSightData } from '@common/components/components';
import { getLineOfSightTiles, hasLineOfSight, TileCoord } from '@common/mapCollisionUtils';
import { Graphics } from 'pixi.js';

import { ClientWorld } from '../ClientWorld';
import { ClientGlobalSystem } from './ClientBase';

const VISIBLE_FILL = 0x00ffd5;
const VISIBLE_STROKE = 0x00ffd5;
const BLOCKED_FILL = 0xff7a59;
const BLOCKED_STROKE = 0xff7a59;
const ORIGIN_FILL = 0xfff07a;
const ORIGIN_STROKE = 0xfff07a;
const TILE_FILL_ALPHA = 0.15;
const TILE_STROKE_ALPHA = 0.8;
const ORIGIN_FILL_ALPHA = 0.75;
const ORIGIN_STROKE_ALPHA = 1;
const LINE_WIDTH = 1;
const OVERLAY_Z_INDEX = 100000.5;

type LineOfSightComponent = {
  readonly typeName: 'LineOfSight';
} & LineOfSightData;

type TransformLike = {
  x: number;
  y: number;
  sceneId: SceneId;
};

type BoundsLike = {
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
};

type WorldWithLineOfSight = ClientWorld & {
  getComponent(entityId: number, typeName: 'LineOfSight'): LineOfSightComponent | undefined;
};

/**
 * Debug system that renders line-of-sight tile cones for all active observers.
 * Toggled via the Debug Panel.
 */
export class DebugLineOfSightRenderSystem extends ClientGlobalSystem {
  private graphics: Graphics | null = null;
  private initialized = false;
  private lastShowState = false;
  private lastSceneId: SceneId | undefined;
  private lastChunkKeys = '';
  private lastObserverSignature = '';
  private pendingRedraw = false;

  run(): ServerMessagePayload[] {
    const world = this.getWorld();
    const showLineOfSight = this.clientServices.debug.state.showLineOfSight;

    if (this.graphics) {
      this.graphics.visible = showLineOfSight;
    }

    if (!showLineOfSight) {
      this.resetRenderCache();
      return [];
    }

    const currentSceneId = this.getCurrentSceneId(world);
    if (!currentSceneId) {
      this.pendingRedraw = true;
      return [];
    }

    const metadata = this.clientServices.chunkData.getSceneMetadata(currentSceneId);
    const chunks = this.clientServices.chunkData.getChunks(currentSceneId);
    if (!metadata || !chunks) {
      this.pendingRedraw = true;
      return [];
    }

    if (!this.graphics) {
      this.graphics = new Graphics();
      this.graphics.zIndex = OVERLAY_Z_INDEX;
      this.clientServices.render.stage?.addChild(this.graphics);
    }

    const currentChunkKeys = Array.from(chunks.keys()).sort().join(',');
    const currentObserverSignature = this.getObserverSignature(world, currentSceneId, metadata);
    const shouldInvalidate =
      !this.initialized ||
      this.lastShowState !== showLineOfSight ||
      this.lastSceneId !== currentSceneId ||
      this.lastChunkKeys !== currentChunkKeys ||
      this.lastObserverSignature !== currentObserverSignature;

    if (shouldInvalidate) {
      this.pendingRedraw = true;
    }

    if (!this.pendingRedraw) {
      return [];
    }

    this.initialized = true;
    this.lastShowState = showLineOfSight;
    this.lastSceneId = currentSceneId;
    this.lastChunkKeys = currentChunkKeys;
    this.lastObserverSignature = currentObserverSignature;
    this.pendingRedraw = false;

    this.renderObservers(world, currentSceneId, metadata);

    return [];
  }

  private resetRenderCache(): void {
    this.initialized = false;
    this.lastShowState = false;
    this.lastSceneId = undefined;
    this.lastChunkKeys = '';
    this.lastObserverSignature = '';
    this.pendingRedraw = true;
  }

  private getCurrentSceneId(world: ClientWorld): SceneId | undefined {
    const localEntityId = this.clientServices.localPlayer.entityId;
    if (localEntityId === undefined) {
      return undefined;
    }

    const transform = this.getEntityTransform(world, localEntityId);
    return transform?.sceneId;
  }

  private getObserverSignature(
    world: ClientWorld,
    sceneId: SceneId,
    metadata: {
      tileWidth: number;
      tileHeight: number;
      chunkSize: number;
      minChunkX: number;
      maxChunkX: number;
      minChunkY: number;
      maxChunkY: number;
    },
  ): string {
    const observers = world.query(['Transform', 'Facing']);
    const parts: string[] = [];

    for (const entityId of observers) {
      const transform = this.getEntityTransform(world, entityId);
      if (!transform || transform.sceneId !== sceneId) {
        continue;
      }

      const facing = world.getComponent(entityId, 'Facing')?.direction;
      const los = this.getLineOfSightComponent(world, entityId);
      const range = this.getLineOfSightRange(los);
      if (!facing || !range) {
        continue;
      }

      const originCenter = this.getEntityCenter(world, entityId);
      if (!originCenter) {
        continue;
      }

      const origin = this.getEntityTile(originCenter, metadata.tileWidth, metadata.tileHeight);
      parts.push(`${entityId}:${origin.col},${origin.row}:${facing}:${range}`);
    }

    return parts.sort().join('|');
  }

  private renderObservers(
    world: ClientWorld,
    sceneId: SceneId,
    metadata: {
      tileWidth: number;
      tileHeight: number;
      chunkSize: number;
      minChunkX: number;
      maxChunkX: number;
      minChunkY: number;
      maxChunkY: number;
    },
  ): void {
    if (!this.graphics) {
      return;
    }

    this.graphics.clear();

    const observers = world.query(['Transform', 'Facing']);

    for (const entityId of observers) {
      const transform = this.getEntityTransform(world, entityId);
      if (!transform || transform.sceneId !== sceneId) {
        continue;
      }

      const facing = world.getComponent(entityId, 'Facing')?.direction;
      const los = this.getLineOfSightComponent(world, entityId);
      const range = this.getLineOfSightRange(los);
      if (!facing || !range) {
        continue;
      }

      const originCenter = this.getEntityCenter(world, entityId);
      if (!originCenter) {
        continue;
      }

      const origin = this.getEntityTile(originCenter, metadata.tileWidth, metadata.tileHeight);

      this.drawOrigin(origin.col, origin.row, metadata.tileWidth, metadata.tileHeight);

      const candidateTiles = getLineOfSightTiles(origin, facing, range).filter(
        (tile) => !this.isOriginTile(tile, origin) && this.isWithinSceneBounds(tile, metadata),
      );

      for (const tile of candidateTiles) {
        const tileCenter = this.getTileCenter(
          tile.col,
          tile.row,
          metadata.tileWidth,
          metadata.tileHeight,
        );
        const visible = hasLineOfSight(
          this.clientServices.chunkData,
          sceneId,
          originCenter,
          tileCenter,
        );

        this.drawTile(
          tile.col,
          tile.row,
          metadata.tileWidth,
          metadata.tileHeight,
          visible ? VISIBLE_FILL : BLOCKED_FILL,
          visible ? VISIBLE_STROKE : BLOCKED_STROKE,
        );
      }
    }
  }

  private getEntityTransform(world: ClientWorld, entityId: number): TransformLike | undefined {
    return (world.getComponent(entityId, 'GlobalTransform') ??
      world.getComponent(entityId, 'Transform')) as TransformLike | undefined;
  }

  private getEntityCenter(world: ClientWorld, entityId: number): TransformLike | undefined {
    const transform = this.getEntityTransform(world, entityId);
    if (!transform) {
      return undefined;
    }

    const bounds = this.getEntityBounds(world, entityId);
    if (!bounds) {
      return transform;
    }

    return {
      x: transform.x + bounds.offsetX + bounds.width / 2,
      y: transform.y + bounds.offsetY + bounds.height / 2,
      sceneId: transform.sceneId,
    };
  }

  private getEntityTile(
    position: { x: number; y: number },
    tileWidth: number,
    tileHeight: number,
  ): TileCoord {
    return {
      col: Math.floor(position.x / tileWidth),
      row: Math.floor(position.y / tileHeight),
    };
  }

  private getTileCenter(
    col: number,
    row: number,
    tileWidth: number,
    tileHeight: number,
  ): { x: number; y: number } {
    return {
      x: col * tileWidth + tileWidth / 2,
      y: row * tileHeight + tileHeight / 2,
    };
  }

  private getLineOfSightComponent(
    world: ClientWorld,
    entityId: number,
  ): LineOfSightComponent | undefined {
    return (world as WorldWithLineOfSight).getComponent(entityId, 'LineOfSight');
  }

  private getLineOfSightRange(los: LineOfSightComponent | undefined): number | undefined {
    const rawRange = los?.tileRange;
    if (rawRange === undefined) {
      return undefined;
    }

    const range = Math.floor(rawRange);
    return range > 0 ? range : undefined;
  }

  private getEntityBounds(world: ClientWorld, entityId: number): BoundsLike | undefined {
    const hitbox = world.getComponent(entityId, 'Hitbox') as BoundsLike | undefined;
    if (hitbox) {
      return hitbox;
    }

    const collider = world.getComponent(entityId, 'Collider') as BoundsLike | undefined;
    return collider;
  }

  private drawOrigin(col: number, row: number, tileWidth: number, tileHeight: number): void {
    if (!this.graphics) {
      return;
    }

    const insetX = tileWidth * 0.25;
    const insetY = tileHeight * 0.25;
    this.graphics.rect(
      col * tileWidth + insetX,
      row * tileHeight + insetY,
      tileWidth - insetX * 2,
      tileHeight - insetY * 2,
    );
    this.graphics.fill({ color: ORIGIN_FILL, alpha: ORIGIN_FILL_ALPHA });
    this.graphics.stroke({ color: ORIGIN_STROKE, alpha: ORIGIN_STROKE_ALPHA, width: LINE_WIDTH });
  }

  private drawTile(
    col: number,
    row: number,
    tileWidth: number,
    tileHeight: number,
    fillColor: number,
    strokeColor: number,
  ): void {
    if (!this.graphics) {
      return;
    }

    this.graphics.rect(col * tileWidth, row * tileHeight, tileWidth, tileHeight);
    this.graphics.fill({ color: fillColor, alpha: TILE_FILL_ALPHA });
    this.graphics.stroke({ color: strokeColor, alpha: TILE_STROKE_ALPHA, width: LINE_WIDTH });
  }

  private isOriginTile(tile: TileCoord, origin: TileCoord): boolean {
    return tile.col === origin.col && tile.row === origin.row;
  }

  private isWithinSceneBounds(
    tile: TileCoord,
    metadata: {
      chunkSize: number;
      minChunkX: number;
      maxChunkX: number;
      minChunkY: number;
      maxChunkY: number;
    },
  ): boolean {
    const minCol = metadata.minChunkX * metadata.chunkSize;
    const maxCol = (metadata.maxChunkX + 1) * metadata.chunkSize - 1;
    const minRow = metadata.minChunkY * metadata.chunkSize;
    const maxRow = (metadata.maxChunkY + 1) * metadata.chunkSize - 1;

    return tile.col >= minCol && tile.col <= maxCol && tile.row >= minRow && tile.row <= maxRow;
  }
}
